import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { VEHICLES, VehicleType } from '@shared/constants/vehicles';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import {
  TuiButtonModule,
  TuiDialogService,
  TuiGroupModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  tap,
  combineLatest,
  map,
  Subject,
  switchMap,
  filter,
  catchError,
  of,
  withLatestFrom,
} from 'rxjs';
import {
  TUI_PROMPT,
  TuiInputModule,
  TuiInputNumberModule,
} from '@taiga-ui/kit';
import { Store } from '@ngxs/store';
import { AsyncPipe } from '@angular/common';

import { AlertService } from '@shared/services/alert.service';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import {
  countPalletsCartsAndBulks,
  getComputedProperties,
  getLoadingPercent,
  getRunningMetersFromCartQuantity,
  getRunningMetersFromUSPallets,
} from '@shared/utils/cargo-calculation';
import { AuthState } from '@store/auth/auth.state';
import { CommonStateService } from '../../services/common-state.service';
import { PreOrdersState } from '../../store/pre-orders.state';
import { ComputedPropsForm } from '../../types';
import { PreOrderCargoPlaceDetails } from '../../../../types/pre-order';
import { PreOrderService } from '../../services/pre-order.service';

type TotalByPlaceType = Record<
  string,
  {
    width: number;
    length: number;
    count: number;
  }
>;

@Component({
  selector: 'app-selection-total-info',
  standalone: true,
  imports: [
    AutocompleteComponent,
    TuiGroupModule,
    ReactiveFormsModule,
    TuiInputNumberModule,
    TuiTextfieldControllerModule,
    TuiInputModule,
    AsyncPipe,
    TuiButtonModule,
  ],
  templateUrl: './selection-total-info.component.html',
  styleUrl: './selection-total-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectionTotalInfoComponent implements OnInit {
  protected readonly vehicles = VEHICLES;

  createEmptyCargo$ = new Subject<void>();
  form = this.fb.group({
    transportType: VehicleType.TRUCK,
    trailerType: '',
  });

  computedProperties = this.fb.group<ComputedPropsForm>({
    totalColdPlaces: this.fb.control<string>({ value: '', disabled: true }),
    totalWarmPlaces: this.fb.control<string>({ value: '', disabled: true }),
    totalBox: this.fb.control<number>({ value: 0, disabled: true }),
  });

  vehicleLoadingPercent = this.fb.control({ value: 0, disabled: true });

  preOrders$ = this.store.select(PreOrdersState.getPreOrders);

  disabled$ = combineLatest([
    this.commonStateService.selectedItems$,
    this.commonStateService.trailerType$,
  ]).pipe(
    map(([preOrders, trailer]) => {
      if (!preOrders.length || !trailer) {
        return true;
      }
      return false;
    }),
  );

  user$ = this.store.select(AuthState.getUser);
  constructor(
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    protected readonly commonStateService: CommonStateService,
    private readonly store: Store,
    private readonly dialogs: TuiDialogService,
    private readonly preOrderService: PreOrderService,
    private readonly alert: AlertService,
    protected readonly cargoCatalogsService: CargoCatalogsService,
  ) {}

  onCreateEmptyCargo() {
    this.createEmptyCargo$
      .pipe(
        switchMap(() =>
          this.dialogs
            .open<boolean>(TUI_PROMPT, {
              data: {
                content: `<h1 class="font-bold text-lg">Подтвердите действие<h1>`,
                yes: 'Создать тикет',
                no: 'Отмена',
              },
            })
            .pipe(filter(submit => submit)),
        ),
        withLatestFrom(this.user$),
        switchMap(([, user]) => {
          const formValue = this.form.getRawValue();
          return this.preOrderService
            .createCargo({
              preOrderIds: this.commonStateService.selectedItems$.value.map(
                i => i.id,
              ) as number[],
              trailerType: formValue.trailerType ?? '',
              transportType: formValue.transportType ?? '',
              divisionId: user?.activeDivisionId ?? 0,
            })
            .pipe(
              catchError(() => {
                this.alert.showError();
                return of(null);
              }),
            );
        }),
        catchError(() => {
          return of(null);
        }),
        tap(() => this.commonStateService.refresh$.next()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  updateComputedProperties() {
    combineLatest([this.preOrders$, this.commonStateService.selectedItemsMap$])
      .pipe(
        tap(([preOrders, selectedIdsMap]) => {
          const selectedPreOrders = preOrders.filter(preOrder =>
            selectedIdsMap.has(preOrder?.id ?? 0),
          );

          this.computedProperties.setValue(
            getComputedProperties(selectedPreOrders),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  updateVehicleLoadingPercent() {
    combineLatest([
      this.preOrders$,
      this.commonStateService.selectedItemsMap$,
      this.form.controls.trailerType.valueChanges,
    ])
      .pipe(
        withLatestFrom(this.cargoCatalogsService.trailersTypes$),
        tap(([[preOrders, selectedIdsMap, trailerType], trailerTypes]) => {
          const selectedPreOrders = preOrders.filter(preOrder =>
            selectedIdsMap.has(preOrder?.id ?? 0),
          );
          if (!trailerType || !selectedPreOrders.length) {
            this.vehicleLoadingPercent.setValue(0);
          } else {
            const trailerTypeDetails = trailerTypes.find(
              trailer => trailer.code === trailerType,
            );
            const places: PreOrderCargoPlaceDetails[] = [];
            selectedPreOrders.forEach(preOrder => {
              places.push(...preOrder.cargoPlacesJson);
            });
            const [pallets, carts, bulk] = countPalletsCartsAndBulks(places);
            const palletsMeters = getRunningMetersFromUSPallets(pallets);
            const cartsMeters = getRunningMetersFromCartQuantity(carts);
            const meters = palletsMeters + cartsMeters + bulk;
            this.vehicleLoadingPercent.setValue(
              getLoadingPercent(meters, trailerTypeDetails?.length ?? 0),
            );
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.onCreateEmptyCargo();
    this.updateComputedProperties();
    this.updateVehicleLoadingPercent();
    this.form.controls.trailerType.valueChanges
      .pipe(
        tap(value => this.commonStateService.trailerType$.next(value)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
